import React, { useEffect, useState } from "react";

/**
 * ShutdownOverlay — full-screen fade-to-black goodnight sequence that
 * plays when the "Good Night Raw Company" Siri shortcut fires. Counts
 * down 30s with a spoken farewell, dimming visuals, and a clean orb
 * dissolve so the owner SEES the wind-down rather than the screen just
 * going black.
 *
 * Triggered when /api/jarvis/pending_shutdown returns shutdown=true.
 * (The Windows helper still runs `shutdown /s /t 0` server-side; this
 * is purely the on-screen choreography.)
 */
export default function ShutdownOverlay({ visible, onFarewellComplete }) {
  const [remaining, setRemaining] = useState(30);
  const [spokenOnce, setSpokenOnce] = useState(false);

  useEffect(() => {
    if (!visible) {
      setRemaining(30);
      setSpokenOnce(false);
      return;
    }
    // Speak the farewell ONCE on appearance
    if (!spokenOnce) {
      try {
        if ("speechSynthesis" in window) {
          const u = new SpeechSynthesisUtterance(
            "Good night, sir. Powering down in thirty seconds. Sweet dreams."
          );
          u.rate = 0.95;
          u.pitch = 0.95;
          window.speechSynthesis.cancel();
          window.speechSynthesis.speak(u);
        }
      } catch { /* ignore */ }
      setSpokenOnce(true);
    }
    const id = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(id);
          onFarewellComplete?.();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [visible, spokenOnce, onFarewellComplete]);

  if (!visible) return null;
  return (
    <div
      data-testid="shutdown-overlay"
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: `radial-gradient(ellipse at center, rgba(0,4,12,${0.6 + (30 - remaining) * 0.013}) 0%, rgba(0,0,0,${0.8 + (30 - remaining) * 0.006}) 80%)`,
        backdropFilter: `blur(${(30 - remaining) * 0.3}px)`,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        color: "var(--rc-gold, #ffb800)",
        fontFamily: "'Share Tech Mono', monospace",
        transition: "background 1s linear, backdrop-filter 1s linear",
        pointerEvents: "none",
      }}
    >
      <div style={{ fontSize: 48, letterSpacing: 8, marginBottom: 32, opacity: 0.95 }}>
        GOOD NIGHT, SIR
      </div>
      <div style={{ fontSize: 16, letterSpacing: 4, marginBottom: 24, opacity: 0.7 }}>
        WINDOWS SHUTDOWN IN PROGRESS · POWER CUTS IN
      </div>
      <div style={{
        fontSize: 96, letterSpacing: 12,
        textShadow: "0 0 28px var(--rc-gold, #ffb800)",
        fontVariantNumeric: "tabular-nums",
      }}>
        {String(remaining).padStart(2, "0")}
      </div>
      <div style={{ fontSize: 11, letterSpacing: 3, marginTop: 36, opacity: 0.5 }}>
        ALL WORK SAVED · CHANNEL CLOSED · SLEEP TIGHT
      </div>
    </div>
  );
}
